import { useState, useEffect, useCallback } from "react";
import { DollarSign, TrendingUp, Clock, Search } from "lucide-react";
import api from "../lib/api";
import { usd, timeAgo, clsx } from "../lib/format";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function AdminFinance() {
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", method: "", status: "completed", search: "" });

  const loadTop = useCallback(async () => {
    const [s, t] = await Promise.all([api.get("/admin/finance/summary"), api.get("/admin/finance/timeseries", { params: { days: 30 } })]);
    setSummary(s.data); setSeries(t.data);
  }, []);
  const loadRows = useCallback(() => api.get("/admin/finance/inflows", { params: filters }).then((r) => setRows(r.data)), [filters]);

  useEffect(() => { loadTop(); }, [loadTop]);
  useEffect(() => { loadRows(); }, [loadRows]);

  if (!summary) return <div className="text-fx-s3">Loading financial data…</div>;

  const cards = [
    { label: "Today", value: summary.today, icon: DollarSign },
    { label: "This Week", value: summary.week, icon: DollarSign },
    { label: "This Month", value: summary.month, icon: TrendingUp },
    { label: "This Year", value: summary.year, icon: TrendingUp },
    { label: "Total Inflow", value: summary.total, icon: DollarSign },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={`finance-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-xl bg-fx-s1 border border-white/5 p-4">
            <div className="flex items-center gap-2 text-fx-s3 text-xs"><c.icon className="h-3.5 w-3.5" /> {c.label}</div>
            <div className="font-mono text-2xl font-bold text-pos mt-1">{usd(c.value)}</div>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl bg-fx-s1 border border-white/5 p-5">
          <h2 className="font-display font-bold mb-4">Inflow Volume (30 days)</h2>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={series}>
              <defs><linearGradient id="fin" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.5} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="day" stroke="#64748B" fontSize={11} /><YAxis stroke="#64748B" fontSize={11} />
              <Tooltip contentStyle={{ background: "#1C1F26", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
              <Area type="monotone" dataKey="amount" stroke="#10B981" fill="url(#fin)" name="Inflow ($)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl bg-fx-s1 border border-white/5 p-5 space-y-3">
          <div className="flex items-center gap-2 text-gold text-sm"><Clock className="h-4 w-4" /> Pending verification</div>
          <div className="font-mono text-2xl font-bold">{usd(summary.pending_value)}</div>
          <div className="text-xs text-fx-s3">{summary.pending_count} awaiting approval</div>
          <div className="pt-2 border-t border-white/5">
            <div className="text-xs uppercase tracking-wider text-fx-s3 mb-2">By method</div>
            {Object.entries(summary.by_method).map(([m, v]) => (
              <div key={m} className="flex justify-between text-sm py-0.5"><span className="capitalize text-fx-s3">{m}</span><span className="font-mono">{usd(v)}</span></div>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="date" data-testid="finance-from" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="h-9 bg-fx-s1 border-white/10 w-40" />
          <Input type="date" data-testid="finance-to" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="h-9 bg-fx-s1 border-white/10 w-40" />
          <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-fx-s3" /><Input data-testid="finance-search" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="Search user / tx" className="pl-9 h-9 bg-fx-s1 border-white/10 w-56" /></div>
          <select data-testid="finance-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="h-9 bg-fx-s1 border border-white/10 rounded-md px-3 text-sm">
            <option value="completed">Completed</option><option value="awaiting_verification">Awaiting</option><option value="rejected">Rejected</option><option value="all">All</option>
          </select>
        </div>
        <div className="rounded-xl bg-fx-s1 border border-white/5 overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-fx-s3 border-b border-white/5">
              <th className="px-4 py-3">Tx ID</th><th className="px-4 py-3">User</th><th className="px-4 py-3">Method</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-fx-s3">No inflows for this filter.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} data-testid={`finance-row-${r.id}`} className="border-b border-white/[0.03]">
                  <td className="px-4 py-3 font-mono text-[11px] text-fx-s3">{r.id.slice(0, 8)}</td>
                  <td className="px-4 py-3 text-xs">{r.user_email || r.user_id}</td>
                  <td className="px-4 py-3 capitalize">{r.provider}{r.coin ? ` · ${r.coin}` : ""}</td>
                  <td className="px-4 py-3 font-mono text-pos">+{usd(r.amount_usd)}</td>
                  <td className="px-4 py-3"><span className={clsx("px-2 py-0.5 rounded text-[11px] font-mono", r.status === "completed" ? "text-pos bg-pos/15" : r.status === "rejected" ? "text-neg bg-neg/15" : "text-gold bg-gold/15")}>{r.status?.replace("_", " ")}</span></td>
                  <td className="px-4 py-3 text-xs text-fx-s3">{timeAgo(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
